/**
 * Google Ads ECL —— 线索表单用户数据采集 + 广告点击标识落地捕获。
 *
 * 职责：
 *   1. 落地时把 gclid / gbraid / wbraid 写入 first-party cookie（90 天），供后续离线导入归因。
 *   2. 给带 [data-ecl-field] 的隐藏字段回填点击标识、提交时间、线索 ID。
 *   3. 表单提交瞬间读取并规范化用户数据，暂存 sessionStorage（Shopify 表单是整页跳转）。
 *   4. 跳转回来的成功页调用 pushSuccess()，把 user_data 推进 dataLayer 供 GTM 上报。
 *
 * 详见 docs/google-ads-ecl-integration.md
 */
(function () {
  'use strict';

  var CLICK_COOKIE = '__ci_gads';
  var CLICK_COOKIE_DAYS = 90;
  var STORAGE_PREFIX = '__ecl_lead_';
  var STORAGE_TTL_MS = 30 * 60 * 1000;
  var CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'];
  var DEFAULT_PHONE_COUNTRY_CODE = '1'; // 美国主市场；E.164 补全用
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var NANP_RE = /^[2-9]\d{2}[2-9]\d{6}$/; // 10 位北美号码：区号与局号首位都在 2-9

  /* ------------------------------ 基础工具 ------------------------------ */

  function readCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function writeCookie(name, value, days) {
    var domain = location.hostname.replace(/^www\./, '');
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; max-age=' +
      60 * 60 * 24 * days +
      '; path=/; domain=.' +
      domain +
      '; samesite=lax' +
      (location.protocol === 'https:' ? '; secure' : '');
  }

  /* --------------------------- 数据规范化 --------------------------- */

  // Google 要求：去首尾空格 + 转小写。无法通过基本校验的一律不上报（不要传空字符串）。
  function normalizeEmail(value) {
    var v = String(value == null ? '' : value)
      .trim()
      .toLowerCase();
    return EMAIL_RE.test(v) ? v : '';
  }

  // Google 要求 E.164：+ 与国家码，无空格/括号/短横线。
  // 拿不准的一律返回空串 —— 宁可不传，也不要用脏号码污染匹配。
  function normalizePhone(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var hasPlus = raw.charAt(0) === '+';
    var digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    if (hasPlus) {
      return digits.length >= 8 && digits.length <= 15 ? '+' + digits : '';
    }
    // 没写国家码的一律按 NANP（美国主市场）补 +1，但必须先确认它真是个 NANP 号码：
    // 区号与局号首位都在 2-9。否则一个法国本地号 "06 12 34 56 78" 会被补成 +10612345678，
    // 变成一个不存在的美国号码去参与 Google 的哈希匹配。
    if (digits.length === 10) {
      return NANP_RE.test(digits) ? '+' + DEFAULT_PHONE_COUNTRY_CODE + digits : '';
    }
    if (digits.length === 11 && digits.charAt(0) === DEFAULT_PHONE_COUNTRY_CODE) {
      return NANP_RE.test(digits.slice(1)) ? '+' + digits : '';
    }
    return '';
  }

  // Google Ads 离线导入接受的时间格式：yyyy-MM-dd HH:mm:ss+|-HH:mm（本地时区）。
  function formatConversionTime(date) {
    function pad(n) {
      return (n < 10 ? '0' : '') + n;
    }
    var offsetMinutes = -date.getTimezoneOffset();
    var sign = offsetMinutes >= 0 ? '+' : '-';
    var abs = Math.abs(offsetMinutes);
    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate()) +
      ' ' +
      pad(date.getHours()) +
      ':' +
      pad(date.getMinutes()) +
      ':' +
      pad(date.getSeconds()) +
      sign +
      pad(Math.floor(abs / 60)) +
      ':' +
      pad(abs % 60)
    );
  }

  function newLeadId() {
    return 'lead-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* --------------------------- 广告点击标识 --------------------------- */

  function readStoredClickIds() {
    var raw = readCookie(CLICK_COOKIE);
    if (!raw) return {};
    var parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      parsed = null;
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  }

  // 新落地（URL 带点击标识）时覆盖写入并重置 90 天；否则沿用已存的，不每页续期。
  function captureClickIds() {
    var params = new URLSearchParams(location.search);
    var stored = readStoredClickIds();
    var fresh = {};
    var found = false;

    for (var i = 0; i < CLICK_KEYS.length; i++) {
      var value = params.get(CLICK_KEYS[i]);
      if (value) {
        fresh[CLICK_KEYS[i]] = value;
        found = true;
      }
    }

    if (!found) return stored;

    fresh.captured_at = formatConversionTime(new Date());
    fresh.landing_page = location.origin + location.pathname;
    writeCookie(CLICK_COOKIE, JSON.stringify(fresh), CLICK_COOKIE_DAYS);
    return fresh;
  }

  /* --------------------------- 表单字段读写 --------------------------- */

  function fieldValue(form, name) {
    var el = form.querySelector('[data-ecl-field="' + name + '"]');
    return el ? el.value : '';
  }

  function setField(form, name, value) {
    var el = form.querySelector('[data-ecl-field="' + name + '"]');
    if (el) el.value = value;
  }

  // 空的隐藏字段会在 Shopify 通知邮件里留下空行，提交前直接摘掉 name。
  function dropEmptyHiddenFields(form) {
    var hidden = form.querySelectorAll('input[type="hidden"][data-ecl-field]');
    for (var i = 0; i < hidden.length; i++) {
      if (!hidden[i].value) hidden[i].removeAttribute('name');
    }
  }

  // 只放 Google 认可的 key，且空值不落键
  // （指南第 5 节：页面不采集的字段应从代码中删除该键，不要传空字符串）。
  // 表单没有邮编 / 国家，凑不齐 first_name+last_name+postal_code+country 的地址匹配组合，
  // 所以不传姓名与地址，避免 "Missing required address fields" 告警。
  function buildUserData(form) {
    var userData = {};
    var email = normalizeEmail(fieldValue(form, 'email'));
    var phone = normalizePhone(fieldValue(form, 'phone'));
    if (email) userData.email = email;
    if (phone) userData.phone_number = phone;
    return userData;
  }

  /* ----------------------- 提交暂存 / 成功上报 ----------------------- */

  function storageKey(kind) {
    return STORAGE_PREFIX + kind;
  }

  function readStored(kind) {
    try {
      return sessionStorage.getItem(storageKey(kind));
    } catch (e) {
      return null; // 隐私模式下 sessionStorage 访问本身就会抛
    }
  }

  // clickIds 由 init() 在页面加载时算好后一路传进来：整页跳转的表单，
  // 加载到提交之间 cookie 不会变，没必要在提交时再解析一次。
  function capture(form, kind, clickIds) {
    var userData = buildUserData(form);
    if (!userData.email) return; // 没有可独立生效的标识就不上报，避免脏转化
    var payload = {
      kind: kind,
      user_data: userData,
      lead_id: fieldValue(form, 'lead_id') || newLeadId(),
      submitted_at: fieldValue(form, 'submitted_at') || formatConversionTime(new Date()),
      click_ids: clickIds,
      page_path: location.pathname,
      ts: Date.now()
    };
    try {
      sessionStorage.setItem(storageKey(kind), JSON.stringify(payload));
    } catch (e) {
      /* 隐私模式 / 存储已满：放弃暂存，本次转化不上报 */
    }
  }

  var pushedKinds = {};

  function pushSuccess(kind) {
    // 同一页面同一类型只推一次：页脚 newsletter 与 newsletter section 会同时命中 posted_successfully。
    if (!kind || pushedKinds[kind]) return;
    var raw = readStored(kind);
    if (!raw) return;

    var payload = null;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      payload = null;
    }
    try {
      sessionStorage.removeItem(storageKey(kind));
    } catch (e) {
      /* 删不掉不影响本次上报；pushedKinds 仍能挡住同页重复推送 */
    }
    if (!payload || !payload.user_data || !payload.user_data.email) return;
    if (!payload.ts || Date.now() - payload.ts > STORAGE_TTL_MS) return; // 过期的暂存不再上报

    pushedKinds[kind] = true;

    // 供 Google tag「手动配置 → JavaScript 变量」路径读取
    window.eclUserData = payload.user_data;

    var event = {
      event: 'ecl_lead',
      ecl_lead_type: kind,
      ecl_lead_id: payload.lead_id || '',
      ecl_submitted_at: payload.submitted_at || '',
      ecl_user_data: payload.user_data
    };
    var clickIds = payload.click_ids || {};
    for (var i = 0; i < CLICK_KEYS.length; i++) {
      if (clickIds[CLICK_KEYS[i]]) event['ecl_' + CLICK_KEYS[i]] = clickIds[CLICK_KEYS[i]];
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }

  /* ------------------------------ 初始化 ------------------------------ */

  function initForm(form, clickIds) {
    if (form.__eclBound) return;
    form.__eclBound = true;

    for (var i = 0; i < CLICK_KEYS.length; i++) {
      if (clickIds[CLICK_KEYS[i]]) setField(form, CLICK_KEYS[i], clickIds[CLICK_KEYS[i]]);
    }
    setField(form, 'lead_id', newLeadId());

    // 捕获阶段绑定：submit 事件只在浏览器原生校验通过后才派发，
    // 表单数据集在事件派发之后才收集，所以这里改隐藏字段仍然生效。
    form.addEventListener(
      'submit',
      function () {
        setField(form, 'submitted_at', formatConversionTime(new Date()));
        capture(form, form.getAttribute('data-ecl-lead'), clickIds);
        dropEmptyHiddenFields(form);
      },
      true
    );
  }

  function init() {
    var clickIds = captureClickIds();
    var forms = document.querySelectorAll('form[data-ecl-lead]');
    for (var i = 0; i < forms.length; i++) {
      initForm(forms[i], clickIds);
    }
  }

  window.eclLead = { pushSuccess: pushSuccess };
  init();

  // 主题编辑器重渲染 section（页脚 / newsletter / contact）后是全新的 <form> 节点，
  // 首次 init() 绑的监听器随旧节点一起没了，这里补绑；initForm 自带 __eclBound 幂等。
  document.addEventListener('shopify:section:load', init);

  // 队列里搬运的就是 kind 字符串本身，见 snippets/google-lead-tracking.liquid。
  // 本文件是 defer：成功分支的内联脚本先执行、入队，这里排空；
  // 之后再 push 的（例如将来 AJAX 提交的表单）直接派发。
  var pending = window.eclLeadQueue;
  window.eclLeadQueue = { push: pushSuccess };
  if (pending) {
    for (var q = 0; q < pending.length; q++) pushSuccess(pending[q]);
  }
})();
